<template>
  <div class="shapes-widget">
    <div class="shapes-options p-[24px]">
      <div class="content">
        <h6 class="text-h6 font-bold text-black">Shapes options</h6>
        <div class="flex flex-wrap gap-y-3 my-5">
          <button
            class="select-shape-button basis-1/3"
            :class="{ active: isRectangle }"
            @click="designerStore.setShape(DieCutMaskShape.Rectangle)"
          >
            <svg
              width="28"
              height="27"
              viewBox="0 0 28 27"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0.935547" width="26.5169" height="26.5169" fill="#7F75B2" />
            </svg>
            <span>Square</span>
          </button>

          <button
            class="select-shape-button basis-1/3"
            :class="{ active: isTriangle }"
            @click="designerStore.setShape(DieCutMaskShape.Triangle)"
          >
            <svg
              width="32"
              height="28"
              viewBox="0 0 32 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M15.8076 0.663086L31.1171 27.18H0.498047L15.8076 0.663086Z" fill="#7F75B2" />
            </svg>
            <span>Triangle</span>
          </button>
          <button
            class="select-shape-button basis-1/3"
            :class="{ active: isCircle }"
            @click="designerStore.setShape(DieCutMaskShape.Circle)"
          >
            <svg
              width="28"
              height="27"
              viewBox="0 0 28 27"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0.548828" width="26.5169" height="26.5169" rx="13.2585" fill="#FFCF57" />
            </svg>
            <span>Circle</span>
          </button>
        </div>
      </div>
    </div>

    <div class="p-[24px]">
      <h6 class="text-h6 font-bold text-black mb-4">Label size</h6>

      <!-- Rectangle inputs -->
      <div v-if="isRectangle" class="flex flex-col gap-4">
        <div class="flex flex-col xl:flex-row gap-4">
          <ui-input
            type="number"
            max="400"
            v-model="designerStore.dieCutMask.width"
            append-text="mm"
            title="Width"
            class="flex-1"
          />
          <ui-input
            type="number"
            v-model="designerStore.dieCutMask.radiusX"
            append-text="mm"
            title="Radius X"
            class="flex-1"
          />
        </div>
        <div class="flex flex-col xl:flex-row gap-4">
          <ui-input
            type="number"
            max="400"
            v-model="designerStore.dieCutMask.height"
            append-text="mm"
            title="Height"
            class="flex-1"
          />
          <ui-input
            type="number"
            v-model="designerStore.dieCutMask.radiusY"
            append-text="mm"
            title="Radius Y"
            class="flex-1"
          />
        </div>
      </div>

      <!-- Triangle inputs -->
      <div v-else-if="isTriangle" class="flex flex-col xl:flex-row gap-4">
        <ui-input
          type="number"
          max="400"
          v-model="designerStore.dieCutMask.width"
          append-text="mm"
          title="Width"
          class="flex-1"
        />
        <ui-input
          type="number"
          max="400"
          v-model="designerStore.dieCutMask.height"
          append-text="mm"
          title="Height"
          class="flex-1"
        />
      </div>

      <!-- Circle input -->
      <div v-else-if="isCircle" class="grid grid-cols-1 gap-4">
        <ui-input
          type="number"
          max="400"
          v-model="designerStore.dieCutMask.width"
          append-text="mm"
          title="Diameter"
          @update:model-value="syncCircleSize"
        />
      </div>
    </div>

    <WidgetTextLayerControl
      v-for="layer in designerStore.textLayers"
      :key="layer.id"
      :layer="layer"
      @update:layer="(updated) => Object.assign(layer, updated)"
      @delete="designerStore.deleteTextLayer($event)"
    />
    <div class="text-right mb-6">
      <button class="btn-base btn-secondary mr-2" @click="designerStore.addTextLayer()">
        <span class="font-bold">+ Add text layer</span>
        <span></span>
      </button>
    </div>

    <button @click="onContinue" class="black-bottom" v-if="!isAdmin">Continue</button>
    <div v-else style="height: 10px"></div>
  </div>

  <div class="lines-legend mt-[16px]">
    <span class="bleed">Bleed</span>
    <span class="die-cut">Die-cut</span>
  </div>
</template>

<script setup lang="ts">
import UiInput from '@/components/ui/UiInput.vue'
import {computed, inject} from 'vue'
import { DieCutMaskShape, useDesignerStore } from '@/stores/designerStore.ts'
import WidgetTextLayerControl from '@/components/designer/widget/WidgetTextLayerControl.vue'
import {useAdminDesignerStore} from "@/stores/adminDesignerStore.ts";
import type {WizardContext} from "@/types/wizard.ts";

const designerStore = useDesignerStore()
const wizard = inject<WizardContext>('wizard')
const isAdmin = computed(() => useAdminDesignerStore().embedded === true)
const isRectangle = computed(() => designerStore.dieCutMask.type === 'rectangle')
const isTriangle = computed(() => designerStore.dieCutMask.type === 'triangle')
const isCircle = computed(() => designerStore.dieCutMask.type === 'circle')
const emit = defineEmits(['continueClicked'])
function syncCircleSize(value: number | string) {
  designerStore.dieCutMask.height = Number(value)
}

const onContinue = () => {
  emit('continueClicked');
}
</script>

<style lang="scss" scoped>
.shapes-widget {
  background: #ffffff;
  border-radius: 32px;
  position: relative;
  z-index: 1;

  .shapes-options {
    z-index: 3;
    background: #ffffff;
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 32px;

    .content {
      position: relative;
      z-index: 4;
    }

    &:before {
      z-index: 2;
      content: '';
      box-sizing: border-box;
      position: absolute;
      left: 8px;
      right: 8px;
      bottom: 8px;
      top: 8px;
      background: #ffffff;
      border: 1px dashed rgba(0, 0, 0, 0.2);
      border-radius: 28px;
    }
  }

  .black-bottom {
    color: #fff;
    background: #141414;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.02em;
    height: 80px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0 0 32px 32px;
  }
}

.lines-legend {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 24px;
  gap: 9.79px;
  font-size: 16px;
  font-weight: 500;

  width: 100%;
  background: #ffffff;
  border: 0.163128px solid #e5e5e5;
  box-shadow: 0px 0.326256px 0.652513px rgba(16, 24, 40, 0.05);
  border-radius: 24px;

  .bleed,
  .die-cut {
    &:before {
      content: '';
      display: inline-block;
      vertical-align: middle;
      margin: auto 5px auto 0;
      width: 58px;
      height: 1px;
    }
  }

  .bleed {
    margin-right: 10px;
    color: #f79009;
    &:before {
      border: 1px dashed #f79009;
    }
  }

  .die-cut {
    color: #000;
    &:before {
      border: 1px solid #000;
    }
  }
}
</style>

<style lang="scss">
.select-shape-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0.3;
  transition: opacity 0.2s ease-in-out;

  svg {
    margin: 0 auto 8px;
  }
  span {
    font-size: 11px;
    line-height: 22px;
    color: #000000;
  }

  &:hover,
  &.active {
    opacity: 1 !important;
  }
}
</style>
